import * as React from "react";

import type { AuditLogEntry } from "api/auditLogs";
import { Badge, CodeValue, DescriptionList, type DescriptionListItem } from "Components/ui";
import { formatDateTime, formatINR } from "utils/format";
import { formatSource, isSystemEntry } from "./actionMeta";

type Snapshot = Record<string, unknown>;

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;
const paise = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

interface ReceiptLine {
  invoiceNumber: string | null;
  amount: number | null;
}

const receiptLines = (value: unknown): ReceiptLine[] =>
  Array.isArray(value)
    ? value.map((line) => {
        const row = (line || {}) as Snapshot;
        return { invoiceNumber: text(row.invoiceNumber), amount: paise(row.amount) };
      })
    : [];

/** Whether an entry is about money (payments, receipts, total repairs): it gets a summary. */
export const isPaymentEntry = (entry: AuditLogEntry): boolean =>
  /^BILLING_PAYMENT_/i.test(entry.action || "");

/**
 * The payment an entry is about, in plain words: amount, invoice, client, how it was paid and how
 * SAP learnt of it. Built from the entry's own snapshot, so it shows what was recorded then.
 */
export const PaymentSummary: React.FC<{ entry: AuditLogEntry }> = ({ entry }) => {
  const after = (entry.changes?.after || {}) as Snapshot;
  const before = (entry.changes?.before || {}) as Snapshot;
  const items: DescriptionListItem[] = [];

  const amount = paise(after.amount);
  const total = paise(after.paymentTotal);
  if (amount !== null) {
    items.push({
      label: "Amount",
      value: formatINR(amount),
      hint:
        total !== null && total > amount
          ? `This invoice's share of a ${formatINR(total)} payment`
          : undefined,
    });
  }
  const invoiceNumber = text(after.invoiceNumber);
  if (invoiceNumber) {
    items.push({
      label: "Invoice",
      value: <span className="font-mono">{invoiceNumber}</span>,
      hint: text(after.invoiceStatus)
        ? `Now ${String(after.invoiceStatus).replace(/_/g, " ")}`
        : undefined,
    });
  }
  const receiptNumber =
    entry.action === "BILLING_PAYMENT_RECEIPT_ISSUED" ? text(after.number) : null;
  if (receiptNumber) {
    items.push({ label: "Receipt", value: <span className="font-mono">{receiptNumber}</span> });
  }
  if (entry.targetTenant) {
    items.push({
      label: "Client",
      value: (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span className="truncate">{entry.targetTenant.name}</span>
          {entry.targetTenant.slug && (
            <Badge className="shrink-0 font-mono">{entry.targetTenant.slug}</Badge>
          )}
        </span>
      ),
    });
  }
  const instrument = text(after.instrument);
  const method = text(after.method);
  const provider = text(after.provider);
  if (instrument || method) {
    items.push({
      label: "Paid with",
      value: (instrument || method || "").replace(/_/g, " "),
      hint: provider
        ? `Through ${provider.charAt(0).toUpperCase()}${provider.slice(1)}`
        : undefined,
    });
  }
  const providerPaymentId = text(after.providerPaymentId);
  if (providerPaymentId) {
    items.push({
      label: "Gateway payment",
      value: <CodeValue value={providerPaymentId} label="gateway payment id" />,
    });
  }
  const statementRef = text(after.statementRef);
  if (statementRef) {
    items.push({ label: "Statement", value: <span className="font-mono">{statementRef}</span> });
  }
  if (text(after.paidAt)) {
    items.push({ label: "Paid at", value: formatDateTime(String(after.paidAt)) });
  }
  items.push({
    label: "Recorded by",
    value: isSystemEntry(entry) ? "System" : entry.actorName || "An admin",
    hint: isSystemEntry(entry) ? formatSource(entry.source) : undefined,
  });
  if (text(after.reviewNote)) {
    items.push({ label: "Needs review", value: String(after.reviewNote) });
  }
  if (text(after.reverseReason)) {
    items.push({ label: "Reversal reason", value: String(after.reverseReason) });
  }
  const expected = paise(after.expected);
  if (expected !== null) {
    items.push({
      label: "Totals",
      value: `Invoice shows ${formatINR(
        paise(after.amountPaid) || 0
      )}, payments add up to ${formatINR(expected)}`,
    });
  }
  if (Array.isArray(after.repairs)) {
    const paidBefore = paise(before.amountPaid);
    items.push({
      label: "Repaired",
      value: `${after.repairs.length} payment ${after.repairs.length === 1 ? "entry" : "entries"}`,
      hint: paidBefore !== null ? `Paid total before: ${formatINR(paidBefore)}` : undefined,
    });
  }

  const lines = receiptLines(after.lines);
  return (
    <div className="space-y-4">
      <DescriptionList items={items} columns={2} />
      {lines.length > 0 && (
        <ul className="divide-y divide-border rounded-control border border-border text-sm">
          {lines.map((line, index) => (
            <li key={index} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="font-mono text-xs">{line.invoiceNumber || "Invoice"}</span>
              <span className="tabular-nums">
                {line.amount !== null ? formatINR(line.amount) : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
